import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, euro } from "../../lib/api";
import { toast } from "sonner";
import {
  format, startOfWeek, endOfWeek, addDays, addWeeks, addMonths,
  startOfMonth, endOfMonth, isSameMonth, isSameDay, startOfToday,
} from "date-fns";
import { nl } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Plus, X, Loader2, Clock, Phone, Mail, StickyNote } from "lucide-react";

const STATUS_STYLE = {
  confirmed: "bg-brand-pink/20 text-brand-charcoal border-brand-pink/40",
  completed: "bg-green-100 text-green-800 border-green-200",
  cancelled: "bg-gray-100 text-gray-400 border-gray-200 line-through",
};
const STATUS_LABEL = { confirmed: "Bevestigd", completed: "Voltooid", cancelled: "Geannuleerd" };

export default function CalendarPage() {
  const [view, setView] = useState("week");
  const [cursor, setCursor] = useState(startOfToday());
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const range = useCallback(() => {
    if (view === "day") return [cursor, cursor];
    if (view === "week") return [startOfWeek(cursor, { weekStartsOn: 1 }), endOfWeek(cursor, { weekStartsOn: 1 })];
    return [startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }), endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 })];
  }, [view, cursor]);

  const load = useCallback(() => {
    const [s, e] = range();
    setLoading(true);
    api.get("/admin/appointments", { params: { start: format(s, "yyyy-MM-dd"), end: format(e, "yyyy-MM-dd") } })
      .then(({ data }) => setAppts(data)).catch(() => {}).finally(() => setLoading(false));
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const move = (dir) => {
    if (view === "day") setCursor(addDays(cursor, dir));
    else if (view === "week") setCursor(addWeeks(cursor, dir));
    else setCursor(addMonths(cursor, dir));
  };

  const apptsFor = (d) => appts.filter((a) => a.date === format(d, "yyyy-MM-dd")).sort((x, y) => x.start_time.localeCompare(y.start_time));

  const [s, e] = range();
  const weekDays = []; for (let d = s; d <= e; d = addDays(d, 1)) weekDays.push(d);

  return (
    <div data-testid="admin-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-4xl font-semibold text-brand-charcoal">Agenda</h1>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 bg-brand-charcoal text-white font-semibold px-5 py-2.5 rounded-full text-sm" data-testid="create-appointment-button">
          <Plus size={16} /> Nieuwe afspraak
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <button onClick={() => move(-1)} className="h-9 w-9 rounded-full border border-brand-border bg-white flex items-center justify-center"><ChevronLeft size={18} /></button>
          <button onClick={() => move(1)} className="h-9 w-9 rounded-full border border-brand-border bg-white flex items-center justify-center"><ChevronRight size={18} /></button>
          <span className="font-display text-xl font-medium capitalize ml-2">
            {view === "day" ? format(cursor, "EEEE d MMMM yyyy", { locale: nl }) : view === "week" ? `${format(s, "d MMM", { locale: nl })} – ${format(e, "d MMM yyyy", { locale: nl })}` : format(cursor, "MMMM yyyy", { locale: nl })}
          </span>
        </div>
        <div className="flex bg-white border border-brand-border rounded-full p-1">
          {["day", "week", "month"].map((v) => (
            <button key={v} onClick={() => setView(v)} data-testid={`calview-${v}`} className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize ${view === v ? "bg-brand-charcoal text-white" : "text-brand-muted"}`}>
              {v === "day" ? "Dag" : v === "week" ? "Week" : "Maand"}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="flex justify-center py-6"><Loader2 className="animate-spin text-brand-pink" size={24} /></div>}

      {view === "month" ? (
        <div className="bg-white rounded-2xl border border-brand-border p-3 sm:p-4">
          <div className="grid grid-cols-7 text-center text-xs text-brand-muted mb-2">
            {["ma", "di", "wo", "do", "vr", "za", "zo"].map((d) => <span key={d}>{d}</span>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {weekDays.map((d, i) => {
              const list = apptsFor(d).filter((a) => a.status !== "cancelled");
              return (
                <div key={i} className={`min-h-[76px] rounded-lg border p-1.5 ${isSameMonth(d, cursor) ? "border-brand-border" : "border-transparent opacity-40"} ${isSameDay(d, new Date()) ? "bg-brand-blush" : "bg-white"}`}>
                  <span className="text-xs font-medium text-brand-charcoal">{format(d, "d")}</span>
                  <div className="space-y-0.5 mt-1">
                    {list.slice(0, 3).map((a) => (
                      <button key={a.id} onClick={() => setSelected(a)} className="w-full text-left text-[10px] leading-tight px-1 py-0.5 rounded bg-brand-pink/20 text-brand-charcoal truncate">
                        {a.start_time} {a.first_name}
                      </button>
                    ))}
                    {list.length > 3 && <span className="text-[10px] text-brand-muted">+{list.length - 3}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className={`grid gap-3 ${view === "day" ? "grid-cols-1" : "grid-cols-1 md:grid-cols-7"}`}>
          {weekDays.map((d, i) => {
            const list = apptsFor(d);
            return (
              <div key={i} className="bg-white rounded-2xl border border-brand-border p-3 min-h-[120px]">
                <p className={`text-sm font-semibold mb-2 capitalize ${isSameDay(d, new Date()) ? "text-brand-pinkhover" : "text-brand-charcoal"}`}>
                  {format(d, "EEE d MMM", { locale: nl })}
                </p>
                <div className="space-y-2">
                  {list.length === 0 && <p className="text-xs text-brand-muted">—</p>}
                  {list.map((a) => (
                    <button key={a.id} onClick={() => setSelected(a)} data-testid={`appt-${a.id}`} className={`w-full text-left rounded-xl border px-2.5 py-2 text-xs ${STATUS_STYLE[a.status]}`}>
                      <p className="font-semibold">{a.start_time} – {a.end_time}</p>
                      <p className="truncate">{a.first_name} {a.last_name}</p>
                      <p className="truncate opacity-80">{a.service_name}</p>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected && <ApptModal appt={selected} onClose={() => setSelected(null)} onChange={() => { load(); }} setSelected={setSelected} />}
      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function ApptModal({ appt, onClose, onChange, setSelected }) {
  const [note, setNote] = useState(appt.internal_note || "");
  const [saving, setSaving] = useState(false);

  const update = async (payload, msg) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/appointments/${appt.id}`, payload);
      toast.success(msg);
      setSelected(data);
      onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Modal onClose={onClose} testid="appt-modal">
      <div className="flex items-start justify-between mb-4">
        <div>
          <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full border ${STATUS_STYLE[appt.status]}`}>{STATUS_LABEL[appt.status]}</span>
          <h2 className="font-display text-2xl font-semibold text-brand-charcoal mt-2">{appt.first_name} {appt.last_name}</h2>
        </div>
        <button onClick={onClose}><X size={20} className="text-brand-muted" /></button>
      </div>
      <div className="space-y-2 text-sm">
        <Row icon={<Clock size={15} />} value={`${appt.date_display} · ${appt.start_time}–${appt.end_time}`} />
        <Row value={`${appt.service_category} · ${appt.service_name} — ${appt.price_display}`} />
        <Row icon={<Phone size={15} />} value={appt.phone} />
        <Row icon={<Mail size={15} />} value={appt.email} />
        {appt.note && <Row icon={<StickyNote size={15} />} value={`Klant: ${appt.note}`} />}
      </div>

      <div className="mt-4">
        <label className="block text-sm font-medium text-brand-charcoal mb-1.5">Interne notitie</label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="appt-internal-note" className="w-full rounded-xl border border-brand-border px-3 py-2 text-sm focus:border-brand-pink focus:outline-none resize-none" />
        <button onClick={() => update({ internal_note: note }, "Notitie opgeslagen")} className="mt-2 text-sm bg-brand-blush text-brand-charcoal font-medium px-4 py-2 rounded-full">Notitie opslaan</button>
      </div>

      <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-brand-border">
        {appt.status !== "completed" && <button disabled={saving} onClick={() => update({ status: "completed" }, "Gemarkeerd als voltooid")} className="text-sm bg-green-600 text-white font-medium px-4 py-2 rounded-full" data-testid="appt-complete">Voltooid</button>}
        {appt.status !== "cancelled" && <button disabled={saving} onClick={() => update({ status: "cancelled" }, "Afspraak geannuleerd")} className="text-sm bg-red-600 text-white font-medium px-4 py-2 rounded-full" data-testid="appt-cancel">Annuleren</button>}
        {appt.status === "cancelled" && <button disabled={saving} onClick={() => update({ status: "confirmed" }, "Opnieuw bevestigd")} className="text-sm bg-brand-charcoal text-white font-medium px-4 py-2 rounded-full">Herbevestigen</button>}
      </div>
    </Modal>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({ service_id: "", date: format(new Date(), "yyyy-MM-dd"), start_time: "10:00", first_name: "", last_name: "", email: "", phone: "", internal_note: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/services/all").then(({ data }) => setServices(data)).catch(() => {}); }, []);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/admin/appointments", form);
      toast.success("Afspraak aangemaakt");
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const valid = form.service_id && form.first_name && form.last_name && form.email && form.phone && form.start_time;

  return (
    <Modal onClose={onClose} testid="create-appt-modal">
      <h2 className="font-display text-2xl font-semibold text-brand-charcoal mb-4">Nieuwe afspraak</h2>
      <div className="space-y-3">
        <select value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })} data-testid="create-service-select" className="w-full rounded-xl border border-brand-border px-3 py-2.5 text-sm focus:border-brand-pink focus:outline-none">
          <option value="">Kies behandeling…</option>
          {services.map((s) => <option key={s.id} value={s.id}>{s.category} · {s.name} ({euro(s.price)})</option>)}
        </select>
        <div className="grid grid-cols-2 gap-3">
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl border border-brand-border px-3 py-2.5 text-sm" data-testid="create-date" />
          <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="rounded-xl border border-brand-border px-3 py-2.5 text-sm" data-testid="create-time" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input placeholder="Voornaam" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-xl border border-brand-border px-3 py-2.5 text-sm" data-testid="create-first-name" />
          <input placeholder="Achternaam" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-xl border border-brand-border px-3 py-2.5 text-sm" data-testid="create-last-name" />
        </div>
        <input placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-brand-border px-3 py-2.5 text-sm" data-testid="create-email" />
        <input placeholder="Telefoon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full rounded-xl border border-brand-border px-3 py-2.5 text-sm" data-testid="create-phone" />
      </div>
      <button disabled={!valid || saving} onClick={submit} className="w-full mt-5 bg-brand-charcoal text-white font-semibold py-3 rounded-full disabled:opacity-40" data-testid="create-submit">
        {saving ? "Bezig…" : "Afspraak aanmaken"}
      </button>
    </Modal>
  );
}

function Modal({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid={testid}>
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function Row({ icon, value }) {
  return <div className="flex items-start gap-2 text-brand-charcoal/80"><span className="text-brand-pink mt-0.5">{icon}</span><span>{value}</span></div>;
}
